import React, { Fragment, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { getChatUsersAction } from '../../redux/actions/chatActions';
import NewChatUser from './newChatUser';
import MessengerBox from './MessengerBox';
import ConversationsList from './Conversations/ConversationsList';
import { useSearchParams } from 'react-router-dom';
import { socket } from "../../utils/socket.js"

const Messenger = () => {

    const dispatch = useDispatch()

    const { loading, users, totalUsers } = useSelector(state => state.chatUsers)

    const [showSearch, setShowSearch] = useState(false);
    const [messengerType, setMessengerType] = useState('personal')
    const [keyword, setKeyword] = useState('');
    const [showNewChatPopup, setShowNewChatPopup] = useState(false);
    const [newUserID, setNewUserID] = useState(null);
    const [newUserData, setNewUserData] = useState({});
    const [perPage, setPerPage] = useState(1000);
    const [queryUserID, setQueryUserID] = useState('');
    const [debouncedUserID, setDebouncedUserID] = useState('');
    const [conversationID, setConversationID] = useState(null);
    const [onlineUsers, setOnlineUsers] = useState([]);

    const [searchParams, setSearchParams] = useSearchParams();

    // ✅ 1. INITIAL LOAD FROM URL (ONLY ONCE)
    useEffect(() => {
        const param = searchParams.get("new_chat");
        const userIdFromURL = searchParams.get("user_id");

        setShowNewChatPopup(param === "true");

        if (userIdFromURL) {
            setQueryUserID(userIdFromURL);
        }
    }, [searchParams]); // 🚀 only once

    // ✅ 2. DEBOUNCE (smooth UX)
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedUserID(queryUserID);
        }, 400); // smooth delay

        return () => clearTimeout(timer);
    }, [queryUserID]);

    useEffect(() => {
        socket.emit("get_online_users");

        socket.on("online_users", (data) => {
            setOnlineUsers(data);
        });

        return () => {
            socket.off("online_users");
        };
    }, []);

    // ✅ 3. FETCH USERS
    useEffect(() => {
        if (showNewChatPopup) {

            const query = {}

            if (perPage) query.perPage = perPage
            if (debouncedUserID) query.user_id = debouncedUserID

            dispatch(getChatUsersAction(query))
        }

        document.title = 'Chats'

    }, [dispatch, showNewChatPopup, perPage, debouncedUserID])

    // ✅ 4. UPDATE URL (without breaking input)
    useEffect(() => {
        const query = {};

        if (showNewChatPopup) query.new_chat = "true";
        if (queryUserID) query.user_id = queryUserID;

        setSearchParams(query);
    }, [showNewChatPopup, queryUserID,setSearchParams]);    return (
        <Fragment>
            <div className="p-4 md:p-6 mt-4">
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col lg:grid lg:grid-cols-[1.2fr_3fr] min-h-[80vh] overflow-hidden">

                {/* LEFT SIDE: Sidebar */}
                <div className={`border-r border-gray-100 p-4 flex flex-col gap-4 ${(conversationID || newUserData?.id) ? 'hidden lg:flex' : 'flex'}`}>
                    
                    {/* Header */}
                    <div className="flex justify-between items-center h-14">
                        {showSearch ? (
                            <div className="flex-1 flex items-center bg-gray-50 border border-gray-100 p-2 px-3 rounded-lg focus-within:border-indigo-300 transition-colors">
                                <input
                                    type="text"
                                    placeholder='Search Chat'
                                    className="w-full bg-transparent outline-none text-sm font-medium text-gray-700 placeholder-gray-400"
                                    value={keyword}
                                    onChange={(e) => setKeyword(e.target.value)}
                                />
                                <button 
                                    onClick={() => { 
                                        setShowSearch(false); 
                                        setKeyword(''); 
                                    }} 
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <i className="fa-solid fa-xmark"></i>
                                </button>
                            </div>
                        ) : (
                            <>
                                <h2 className='text-xl font-bold text-gray-800'>Chats</h2>
                                <button
                                    className="w-10 h-10 bg-gray-50 text-gray-500 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
                                    onClick={() => setShowSearch(true)}
                                >
                                    <i className="fa-solid fa-magnifying-glass text-sm"></i>
                                </button>
                            </>
                        )}
                    </div>

                    {/* Tabs */}
                    <div className="grid grid-cols-2 bg-gray-50 p-1 rounded-lg border border-gray-100">
                        <button
                            className={`flex items-center justify-center font-bold text-sm p-2 rounded-md transition-all ${messengerType === "personal" ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                            onClick={() => setMessengerType('personal')}
                        >
                            <i className="fa-regular fa-circle-user text-base"></i>
                            &nbsp;&nbsp; Personal
                        </button>

                        <button
                            className={`flex items-center justify-center font-bold text-sm p-2 rounded-md transition-all ${messengerType === "group" ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                            onClick={() => setMessengerType('group')}
                        >
                            <i className="fa-solid fa-people-group text-base"></i>
                            &nbsp;&nbsp; Group
                        </button>
                    </div>

                    {/* Conversations List */}
                    <div className="flex-1 overflow-y-auto max-h-[50vh] my-scroll">
                        <ConversationsList
                            keyword={keyword}
                            conversationID={conversationID}
                            setConversationID={setConversationID}
                            setNewUserID={setNewUserID}
                            setNewUserData={setNewUserData}
                        />
                    </div>

                    {/* New Chat Button */}
                    <button
                        className="text-white rounded-lg bg-indigo-600 p-2.5 w-full font-bold text-sm hover:bg-indigo-700 transition-colors shadow-sm mt-auto"
                        onClick={() => setShowNewChatPopup(true)}
                    >
                        <i className="fa-solid fa-plus mr-1"></i> New Chat
                    </button>
                </div>

                {/* RIGHT SIDE: Chat Area */}
                <div className={`flex flex-col bg-gray-50/50 ${(conversationID || newUserData?.id) ? 'flex' : 'hidden lg:flex'}`}>
                    <MessengerBox
                        newUserData={newUserData}
                        conversationID={conversationID}
                        setNewUserData={setNewUserData}
                        setConversationID={setConversationID}
                    />
                </div>
            </div>

            {/* POPUP: Contact List */}
            {showNewChatPopup && (
                <section className="fixed inset-0 z-[999999999] bg-black/50 backdrop-blur-sm flex justify-center items-start pt-[5vmax]">
                    <div className="w-[90%] md:w-[60%] lg:w-[35%] bg-white rounded-2xl p-[2vmax] shadow-xl animate-[fadeIn_.2s_ease] border border-gray-100">
                        
                        {/* Header */}
                        <div className="flex justify-between items-center mb-[1.5vmax]">
                            <div>
                                <h2 className='text-[1.4vmax] font-bold text-gray-800'>Contact List</h2>
                                <p className='text-[0.85vmax] text-gray-500'>Browse and select contacts</p>
                            </div>
                            <button
                                className="w-[2.5vmax] h-[2.5vmax] bg-gray-50 text-gray-500 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
                                onClick={() => setShowNewChatPopup(false)}
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        {/* SEARCH INPUT */}
                        <div className="w-full flex items-center bg-gray-50 border border-gray-100 p-[0.7vmax_1vmax] rounded-lg focus-within:border-indigo-300 transition-colors mb-[1.5vmax]">
                            <i className="fa-solid fa-magnifying-glass text-gray-400 mr-[0.5vmax]"></i>
                            <input
                                type="text"
                                placeholder='Search with User ID'
                                className='w-full bg-transparent outline-none text-[0.9vmax] font-medium text-gray-700 placeholder-gray-400'
                                value={queryUserID}
                                onChange={(e) => setQueryUserID(e.target.value)}
                            />
                        </div>

                        <h3 className='text-[0.8vmax] font-bold text-gray-400 uppercase tracking-wider mb-[0.5vmax]'>
                            {totalUsers || 0} persons available
                        </h3>

                        {/* USERS LIST */}
                        <div className="max-h-[40vh] overflow-y-auto my-scroll pr-[0.5vmax] mb-[1.5vmax]">
                            {users?.length > 0 ? (
                                users.map(user => (
                                    <NewChatUser
                                        key={user._id}
                                        user={user}
                                        newUserID={newUserID}
                                        setNewUserID={setNewUserID}
                                        setNewUserData={setNewUserData}
                                        setConversationID={setConversationID}
                                        isOnline={onlineUsers.includes(String(user.id))}
                                    />
                                ))
                            ) : (
                                !loading && <h2 className='text-center text-gray-500 my-[1vmax] text-[0.9vmax]'>No User Found</h2>
                            )}

                            {!loading && totalUsers > perPage && (
                                <div className="flex justify-center mt-[1vmax]">
                                    <button
                                        className="text-[0.85vmax] font-bold text-indigo-600 hover:text-indigo-700"
                                        onClick={() => setPerPage(prev => prev + 20)}
                                    >
                                        Load More
                                    </button>
                                </div>
                            )}

                            {loading && (
                                <div className="w-[2vmax] h-[2vmax] border-4 border-gray-200 border-t-indigo-600 rounded-full animate-spin mx-auto my-[2vmax]" />
                            )}
                        </div>

                        {/* Action Button */}
                        <button
                            className={`text-white rounded-lg p-[0.8vmax_1vmax] w-full font-bold text-[0.9vmax] transition-colors ${newUserID ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-300 cursor-not-allowed'}`}
                            onClick={() => setShowNewChatPopup(false)}
                            disabled={!newUserID}
                        >
                            Start Conversation
                        </button>
                    </div>
                </section>
            )}
            </div>
        </Fragment>
    )
}

export default Messenger